import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Camera, Instagram, Heart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    product_name: '',
    category: 'woodwork_and_art',
    caption: '',
    instagram_handle: ''
  });
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredPhotos(photos);
    } else {
      setFilteredPhotos(photos.filter(p => p.category === selectedCategory));
    }
  }, [selectedCategory, photos]);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customer-photos`);
      setPhotos(response.data);
      setFilteredPhotos(response.data);
    } catch (error) {
      console.error('Error fetching customer photos:', error);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/customer-photos`, formData);
      setSubmitMessage('Thank you for sharing! Your photo has been added to our gallery.');
      setFormData({
        customer_name: '',
        product_name: '',
        category: 'woodwork_and_art',
        caption: '',
        instagram_handle: ''
      });
      setShowSubmitForm(false);
      fetchPhotos();
      setTimeout(() => setSubmitMessage(''), 5000);
    } catch (error) {
      setSubmitMessage('Error submitting photo. Please try again.');
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  const categories = [
    { value: 'all', label: 'All Photos' },
    { value: 'woodwork_and_art', label: 'Woodwork & Art' },
    { value: 'mandirs', label: 'Bespoke Mandirs' },
    { value: 'copperware', label: 'Artisan Metals' },
    { value: 'candles', label: 'Ayurvedic Aromas' },
  ];

  const colorPalettes = [
    ['#B87333', '#D7C0A0'],
    ['#8D7B68', '#EBE5DA'],
    ['#A05A20', '#F5F2EB'],
    ['#5D4037', '#D7C0A0'],
    ['#B87333', '#EBE5DA'],
    ['#8D7B68', '#F5F2EB'],
  ];

  return (
    <div className="py-20 md:py-32" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Camera className="w-8 h-8 text-[#B87333]" />
            <h1 className="text-5xl md:text-7xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="gallery-title">
              Customer Gallery
            </h1>
          </div>
          <p className="text-lg text-[#5D4037] mb-8" data-testid="gallery-subtitle">
            See how our community brings Rasaa products into their daily lives
          </p>
          <button
            onClick={() => setShowSubmitForm(!showSubmitForm)}
            className="btn-primary"
            data-testid="share-photo-button"
          >
            {showSubmitForm ? 'Close Form' : 'Share Your Photo'}
          </button>
        </div>

        {submitMessage && (
          <div className="bg-[#B87333]/10 border-l-2 border-[#B87333] p-4 mb-8 text-[#2C1810]" data-testid="submit-message">
            {submitMessage}
          </div>
        )}

        {showSubmitForm && (
          <div className="bg-white/50 border border-[#D7C0A0] p-8 mb-12 max-w-2xl mx-auto" data-testid="photo-submit-form">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Share Your Heritage Artisan Moment
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Your Name</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  className="w-full bg-white border border-[#D7C0A0] px-4 py-3 rounded-sm focus:border-[#B87333] focus:outline-none"
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Product Name</label>
                <input
                  type="text"
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                  className="w-full bg-white border border-[#D7C0A0] px-4 py-3 rounded-sm focus:border-[#B87333] focus:outline-none"
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-white border border-[#D7C0A0] px-4 py-3 rounded-sm focus:border-[#B87333] focus:outline-none"
                  required
                  data-testid="category-select"
                >
                  <option value="woodwork_and_art">Woodwork & Art</option>
                  <option value="mandirs">Bespoke Mandirs</option>
                  <option value="copperware">Artisan Metals</option>
                  <option value="candles">Ayurvedic Aromas</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Caption</label>
                <textarea
                  value={formData.caption}
                  onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                  rows="3"
                  className="w-full bg-white border border-[#D7C0A0] px-4 py-3 rounded-sm focus:border-[#B87333] focus:outline-none"
                  required
                  data-testid="caption-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Instagram Handle (Optional)</label>
                <input
                  type="text"
                  value={formData.instagram_handle}
                  onChange={(e) => setFormData({ ...formData, instagram_handle: e.target.value })}
                  placeholder="@yourhandle"
                  className="w-full bg-white border border-[#D7C0A0] px-4 py-3 rounded-sm focus:border-[#B87333] focus:outline-none"
                  data-testid="instagram-handle-input"
                />
              </div>
              <button type="submit" className="btn-primary w-full" data-testid="submit-photo-button">
                Submit Photo
              </button>
            </form>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="category-filters">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-6 py-2 rounded-sm transition-colors ${
                selectedCategory === cat.value
                  ? 'bg-[#B87333] text-white'
                  : 'bg-white/50 border border-[#D7C0A0] text-[#2C1810] hover:bg-[#EBE5DA]'
              }`}
              data-testid={`filter-${cat.value}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="photos-grid">
          {filteredPhotos.map((photo, index) => {
            const colors = colorPalettes[index % colorPalettes.length];
            return (
              <div
                key={photo.id}
                className="bg-white/40 border border-[#D7C0A0] overflow-hidden group hover:shadow-xl transition-shadow duration-300"
                data-testid={`photo-card-${photo.id}`}
              >
                <div
                  className="h-64 flex items-center justify-center relative overflow-hidden"
                  style={!photo.image_url ? {
                    background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`
                  } : {}}
                >
                  {photo.image_url ? (
                    <img src={photo.image_url} alt={photo.product_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-white/60 text-sm font-medium">
                      <Camera className="w-12 h-12 mx-auto mb-2 opacity-40" />
                      <p className="uppercase tracking-wider">Customer Photo</p>
                    </div>
                  )}
                  <div className="absolute top-4 right-4 bg-white/90 px-3 py-1 rounded-full text-xs font-medium text-[#2C1810]">
                    {photo.category === 'woodwork_and_art' ? 'Woodwork & Art' : 
                     photo.category === 'mandirs' ? 'Bespoke Mandirs' : 
                     photo.category === 'copperware' ? 'Artisan Metals' : 
                     photo.category === 'candles' ? 'Ayurvedic Aromas' : 
                     photo.category}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-[#2C1810]" data-testid="photo-customer-name">{photo.customer_name}</p>
                      {photo.instagram_handle && (
                        <a
                          href={`https://instagram.com/${photo.instagram_handle.replace('@', '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#B87333] hover:underline flex items-center gap-1"
                          data-testid="photo-instagram-link"
                        >
                          <Instagram className="w-3 h-3" />
                          {photo.instagram_handle}
                        </a>
                      )}
                    </div>
                    <Heart className="w-5 h-5 text-[#B87333]" />
                  </div>
                  <p className="text-sm text-[#5D4037] mb-3 italic" data-testid="photo-caption">"{photo.caption}"</p>
                  <p className="text-xs text-[#8D7B68]" data-testid="photo-product-name">{photo.product_name}</p>
                </div>
              </div>
            );
          })}
        </div>

        {filteredPhotos.length === 0 && (
          <p className="text-center text-[#5D4037] text-lg" data-testid="no-photos-message">
            No photos in this category yet. Be the first to share!
          </p>
        )}
      </div>
    </div>
  );
};

export default CustomerGallery;